import { isIntuitTokenUrl } from './intuitBankToken';

/** Raw card fields go only to an exact, backend-authorized Intuit token endpoint. */
export const CARD_TOKEN_URL = 'https://sandbox.api.intuit.com/quickbooks/v4/payments/tokens';
export const isIntuitCardTokenUrl = isIntuitTokenUrl;
const cardDigits = (value) => String(value || '').replace(/[ -]/g, '');
const unavailable = 'Card payments are unavailable. No payment was submitted.';
const rejected = 'The card details could not be verified. No payment was submitted.';
const safeMessages = new WeakMap();
class CardTokenError extends Error {
  constructor(message) {
    super(message);
    safeMessages.set(this, message);
  }
}
export const tokenizationErrorMessage = (error) => safeMessages.get(error) || null;

export function validateCard(form, now = new Date()) {
  if (form.name.trim().length > 30) return 'Name on card must be 30 characters or fewer.';
  if (!/^\d{13,19}$/.test(cardDigits(form.number))) return 'Enter a valid card number.';
  if (!/^(0?[1-9]|1[0-2])$/.test(form.expMonth) || !/^\d{4}$/.test(form.expYear))
    return 'Enter the card expiration month and four-digit year.';
  if (
    Number(form.expYear) < now.getFullYear() ||
    (Number(form.expYear) === now.getFullYear() && Number(form.expMonth) < now.getMonth() + 1)
  )
    return 'The card expiration date has passed.';
  if (!/^\d{3,4}$/.test(form.cvc)) return 'Enter the card security code.';
  if (form.postalCode && !/^\d{5}(?:-\d{4})?$/.test(form.postalCode.trim()))
    return 'Enter a valid billing ZIP code.';
  if (!form.authorized) return 'Select the checkbox to authorize this card purchase.';
  return '';
}

export function tokenBody(form) {
  return {
    card: {
      ...(form.name.trim() ? { name: form.name.trim() } : {}),
      number: cardDigits(form.number),
      expMonth: form.expMonth.padStart(2, '0'),
      expYear: form.expYear,
      cvc: form.cvc,
      ...(form.postalCode.trim()
        ? { address: { postalCode: form.postalCode.trim(), country: 'US' } }
        : {}),
    },
  };
}

export async function tokenize(tokenUrl, form) {
  if (!isIntuitCardTokenUrl(tokenUrl)) throw new CardTokenError(unavailable);
  if (validateCard(form)) throw new CardTokenError(rejected);
  const requestId = window.crypto?.randomUUID?.();
  if (!requestId) throw new CardTokenError(unavailable);
  let response;
  try {
    response = await fetch(tokenUrl, {
      method: 'POST',
      credentials: 'omit',
      redirect: 'error',
      headers: { 'Content-Type': 'application/json', 'Request-Id': requestId },
      body: JSON.stringify(tokenBody(form)),
      signal: AbortSignal.timeout ? AbortSignal.timeout(30000) : undefined,
    });
    const body = await response.json();
    if (
      !response.ok ||
      typeof body?.value !== 'string' ||
      !body.value ||
      body.value.length > 200 ||
      Array.from(body.value).some(
        (character) => character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127
      )
    )
      throw new CardTokenError(rejected);
    return body.value;
  } catch {
    throw new CardTokenError(rejected);
  }
}
